import React, { useState, useEffect } from 'react';
import {
  Layout,
  Menu,
  Typography,
  Button,
  Card,
  List,
  Avatar,
  Row,
  Col,
  Statistic,
  Badge,
  Modal,
  message,
  Progress,
} from 'antd';
import {
  DashboardOutlined,
  BookOutlined,
  ProjectOutlined,
  TrophyOutlined,
  TeamOutlined,
  MessageOutlined,
  SolutionOutlined, // Replaced JobOutlined with SolutionOutlined
  FundOutlined,
  SettingOutlined,
  LogoutOutlined,
  UserOutlined,
  FileTextOutlined,
  PlusOutlined, // Added PlusOutlined to fix the undefined error
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios'; // Adjust path to your Axios instance

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { SubMenu } = Menu;

const StudentDashboard = () => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [courses, setCourses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [internships, setInternships] = useState([]);

  // Sample data for initial render (replace with API calls)
  const initialCourses = [
    { id: 1, title: 'AI Basics', progress: 75, status: 'Enrolled', instructor: 'Jane Smith' },
    { id: 2, title: 'Cybersecurity 101', progress: 20, status: 'Enrolled', instructor: 'Bob Johnson' },
  ];

  const initialProjects = [
    { id: 1, title: 'AI Chatbot', status: 'In Progress', deadline: '2025-04-01' },
    { id: 2, title: 'Portfolio Website', status: 'Completed', deadline: '2025-03-01' },
  ];

  const initialChallenges = [
    { id: 1, title: 'AI Image Recognition', status: 'Active', daysLeft: 20, prize: '$500' },
    { id: 2, title: 'Cybersecurity Challenge', status: 'Submitted', daysLeft: 0, prize: '$400' },
  ];

  const initialMentors = [
    { id: 1, name: 'Jane Smith', expertise: 'AI', lastMessage: 'Great progress on your project!' },
    { id: 2, name: 'Bob Johnson', expertise: 'Cybersecurity', lastMessage: 'Check your assignment.' },
  ];

  const initialNotifications = [
    { id: 1, message: 'New course available: Data Science Intro', date: '2025-03-05 10:00' },
    { id: 2, message: 'Challenge deadline approaching: AI Image Recognition', date: '2025-03-04 15:00' },
  ];

  const initialJobs = [
    { id: 1, title: 'Junior AI Developer', company: 'TechAI Labs', status: 'Applied', dateApplied: '2025-03-01' },
  ];

  const initialInternships = [
    { id: 1, title: 'AI Research Intern', company: 'SecureAI', status: 'Open', deadline: '2025-03-15' },
  ];

  // Fetch data from backend
  useEffect(() => {
    // Simulate API calls
    setCourses(initialCourses);
    setProjects(initialProjects);
    setChallenges(initialChallenges);
    setMentors(initialMentors);
    setNotifications(initialNotifications);
    setJobs(initialJobs);
    setInternships(initialInternships);

    // Real API calls (uncomment when ready)
    /*
    api.get('/student/courses').then((response) => setCourses(response.data)).catch(console.error);
    api.get('/student/projects').then((response) => setProjects(response.data)).catch(console.error);
    api.get('/student/challenges').then((response) => setChallenges(response.data)).catch(console.error);
    api.get('/student/mentors').then((response) => setMentors(response.data)).catch(console.error);
    api.get('/student/notifications').then((response) => setNotifications(response.data)).catch(console.error);
    api.get('/student/jobs').then((response) => setJobs(response.data)).catch(console.error);
    api.get('/student/internships').then((response) => setInternships(response.data)).catch(console.error);
    */
  }, []);

  const handleMenuClick = (e) => {
    switch (e.key) {
      case 'dashboard':
        navigate('/student/dashboard');
        break;
      case 'courses-all':
        navigate('/student/courses');
        break;
      case 'courses-enrolled':
        navigate('/student/courses/enrolled');
        break;
      case 'courses-available':
        navigate('/student/courses/available');
        break;
      case 'projects-all':
        navigate('/student/projects');
        break;
      case 'projects-active':
        navigate('/student/projects/active');
        break;
      case 'projects-completed':
        navigate('/student/projects/completed');
        break;
      case 'challenges-all':
        navigate('/student/challenges');
        break;
      case 'challenges-active':
        navigate('/student/challenges/active');
        break;
      case 'challenges-submitted':
        navigate('/student/challenges/submitted');
        break;
      case 'mentorship-all':
        navigate('/student/mentorship');
        break;
      case 'mentorship-messages':
        navigate('/student/mentorship/messages');
        break;
      case 'mentorship-find':
        navigate('/student/mentorship/find');
        break;
      case 'community-forum':
        navigate('/student/community/forum');
        break;
      case 'community-events':
        navigate('/student/community/events');
        break;
      case 'jobs-all':
        navigate('/student/jobs');
        break;
      case 'jobs-applied':
        navigate('/student/jobs/applied');
        break;
      case 'internships-all':
        navigate('/student/internships');
        break;
      case 'internships-applied':
        navigate('/student/internships/applied');
        break;
      case 'skills-profile':
        navigate('/student/skills/profile');
        break;
      case 'skills-assessments':
        navigate('/student/skills/assessments');
        break;
      case 'settings':
        navigate('/student/settings');
        break;
      case 'logout':
        localStorage.removeItem('userRole');
        navigate('/signin');
        break;
      default:
        break;
    }
  };

  const handleEnrollCourse = (courseId) => {
    Modal.confirm({
      title: 'Enroll in Course',
      content: 'Are you sure you want to enroll in this course?',
      onOk: () => {
        api
          .post(`/student/courses/enroll/${courseId}`)
          .then(() => {
            setCourses(courses.map((c) => (c.id === courseId ? { ...c, status: 'Enrolled' } : c)));
            message.success('Successfully enrolled in the course');
          })
          .catch((error) => message.error('Failed to enroll: ' + error.message));
      },
    });
  };

  const handleApplyJob = (jobId) => {
    Modal.confirm({
      title: 'Apply for Job/Internship',
      content: 'Are you sure you want to apply for this opportunity?',
      onOk: () => {
        api
          .post(`/student/jobs/apply/${jobId}`)
          .then(() => {
            setJobs(jobs.map((j) => (j.id === jobId ? { ...j, status: 'Applied' } : j)));
            setInternships(internships.map((i) => (i.id === jobId ? { ...i, status: 'Applied' } : i)));
            message.success('Successfully applied');
          })
          .catch((error) => message.error('Failed to apply: ' + error.message));
      },
    });
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Left Sidebar with Child Items */}
      <Sider collapsible collapsed={collapsed} onCollapse={(value) => setCollapsed(value)}>
        <div style={{ height: 32, margin: 16, background: 'rgba(255, 255, 255, 0.2)' }} /> {/* Logo placeholder */}
        <Menu
          theme="dark"
          defaultSelectedKeys={['dashboard']}
          mode="inline"
          onClick={handleMenuClick}
        >
          <Menu.Item key="dashboard" icon={<DashboardOutlined />}>
            Dashboard
          </Menu.Item>
          <SubMenu key="courses" icon={<BookOutlined />} title="Courses">
            <Menu.Item key="courses-all">All Courses</Menu.Item>
            <Menu.Item key="courses-enrolled">Enrolled Courses</Menu.Item>
            <Menu.Item key="courses-available">Available Courses</Menu.Item>
          </SubMenu>
          <SubMenu key="projects" icon={<ProjectOutlined />} title="Projects">
            <Menu.Item key="projects-all">All Projects</Menu.Item>
            <Menu.Item key="projects-active">Active Projects</Menu.Item>
            <Menu.Item key="projects-completed">Completed Projects</Menu.Item>
          </SubMenu>
          <SubMenu key="challenges" icon={<TrophyOutlined />} title="Challenges">
            <Menu.Item key="challenges-all">All Challenges</Menu.Item>
            <Menu.Item key="challenges-active">Active Challenges</Menu.Item>
            <Menu.Item key="challenges-submitted">Submitted Challenges</Menu.Item>
          </SubMenu>
          <SubMenu key="mentorship" icon={<TeamOutlined />} title="Mentorship">
            <Menu.Item key="mentorship-all">My Mentors</Menu.Item>
            <Menu.Item key="mentorship-messages">Messages</Menu.Item>
            <Menu.Item key="mentorship-find">Find a Mentor</Menu.Item>
          </SubMenu>
          <SubMenu key="community" icon={<MessageOutlined />} title="Community">
            <Menu.Item key="community-forum">Forum</Menu.Item>
            <Menu.Item key="community-events">Events</Menu.Item>
          </SubMenu>
          <SubMenu key="jobs" icon={<SolutionOutlined />} title="Jobs">
            <Menu.Item key="jobs-all">All Jobs</Menu.Item>
            <Menu.Item key="jobs-applied">Applied Jobs</Menu.Item>
          </SubMenu>
          <SubMenu key="internships" icon={<FundOutlined />} title="Internships">
            <Menu.Item key="internships-all">All Internships</Menu.Item>
            <Menu.Item key="internships-applied">Applied Internships</Menu.Item>
          </SubMenu>
          <SubMenu key="skills" icon={<UserOutlined />} title="Skills">
            <Menu.Item key="skills-profile">Profile</Menu.Item>
            <Menu.Item key="skills-assessments">Assessments</Menu.Item>
          </SubMenu>
          <Menu.Item key="settings" icon={<SettingOutlined />}>
            Settings
          </Menu.Item>
          <Menu.Item key="logout" icon={<LogoutOutlined />}>
            Logout
          </Menu.Item>
        </Menu>
      </Sider>

      {/* Main Layout */}
      <Layout className="site-layout">
        {/* Header */}
        <Header
          style={{
            padding: '0 20px',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Title level={3} style={{ margin: 0 }}>
            Student Dashboard
          </Title>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/student/projects/new')}>
            Start New Project
          </Button>
        </Header>

        {/* Content */}
        <Content style={{ margin: '24px 16px', padding: 24, background: '#fff', minHeight: 280 }}>
          <Row gutter={[16, 16]}>
            {/* Overview Stats */}
            <Col span={24}>
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic title="Enrolled Courses" value={courses.length} prefix={<BookOutlined />} />
                </Col>
                <Col span={6}>
                  <Statistic title="Active Projects" value={projects.filter((p) => p.status === 'In Progress').length} prefix={<ProjectOutlined />} />
                </Col>
                <Col span={6}>
                  <Statistic title="Active Challenges" value={challenges.filter((c) => c.status === 'Active').length} prefix={<TrophyOutlined />} />
                </Col>
                <Col span={6}>
                  <Statistic title="Notifications" value={notifications.length} prefix={<MessageOutlined />} />
                </Col>
              </Row>
            </Col>

            {/* Courses List */}
            <Col span={16}>
              <Card title="Your Courses" bordered={false}>
                <List
                  itemLayout="horizontal"
                  dataSource={courses.slice(0, 5)}
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <Button type="link" onClick={() => navigate(`/student/courses/${item.id}`)}>
                          View
                        </Button>,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<Avatar icon={<BookOutlined />} />}
                        title={item.title}
                        description={
                          <>
                            <Text>{`Instructor: ${item.instructor} | Status: ${item.status}`}</Text>
                            <Progress percent={item.progress} size="small" style={{ marginTop: 8 }} />
                          </>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Card>

              {/* Projects List */}
              <Card title="Your Projects" bordered={false} style={{ marginTop: 16 }}>
                <List
                  itemLayout="horizontal"
                  dataSource={projects.slice(0, 5)}
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <Button type="link" onClick={() => navigate(`/student/projects/${item.id}`)}>
                          View
                        </Button>,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<Avatar icon={<ProjectOutlined />} />}
                        title={item.title}
                        description={`Status: ${item.status} | Deadline: ${item.deadline}`}
                      />
                    </List.Item>
                  )}
                />
              </Card>

              {/* Challenges List */}
              <Card title="Your Challenges" bordered={false} style={{ marginTop: 16 }}>
                <List
                  itemLayout="horizontal"
                  dataSource={challenges.slice(0, 5)}
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <Button type="link" onClick={() => navigate(`/student/challenges/${item.id}`)}>
                          View
                        </Button>,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<Avatar icon={<TrophyOutlined />} />}
                        title={item.title}
                        description={`Status: ${item.status} | Days Left: ${item.daysLeft} | Prize: ${item.prize}`}
                      />
                    </List.Item>
                  )}
                />
              </Card>
            </Col>

            {/* Right Sidebar */}
            <Col span={8}>
              <Card title="Notifications" bordered={false} style={{ marginBottom: 16 }}>
                <List
                  dataSource={notifications.slice(0, 5)}
                  renderItem={(item) => (
                    <List.Item>
                      <Badge status="processing" text={`${item.message} - ${item.date}`} />
                    </List.Item>
                  )}
                />
              </Card>

              <Card title="Mentors" bordered={false} style={{ marginBottom: 16 }}>
                <List
                  dataSource={mentors.slice(0, 3)}
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <Button type="link" onClick={() => navigate(`/student/mentorship/messages/${item.id}`)}>
                          Message
                        </Button>,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<Avatar icon={<TeamOutlined />} />}
                        title={item.name}
                        description={`Expertise: ${item.expertise} | Last: ${item.lastMessage}`}
                      />
                    </List.Item>
                  )}
                />
              </Card>

              <Card title="Job & Internship Opportunities" bordered={false}>
                <List
                  dataSource={[...jobs.slice(0, 2), ...internships.slice(0, 2)]}
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        item.status === 'Open' ? (
                          <Button type="link" onClick={() => handleApplyJob(item.id)}>
                            Apply
                          </Button>
                        ) : (
                          <Text>{item.status}</Text>
                        ),
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<Avatar icon={item.company ? <SolutionOutlined /> : <FundOutlined />} />}
                        title={item.title}
                        description={item.company ? `${item.company} | ${item.dateApplied || 'Open'}` : `Deadline: ${item.deadline}`}
                      />
                    </List.Item>
                  )}
                />
              </Card>
            </Col>
          </Row>
        </Content>
      </Layout>
    </Layout>
  );
};

export default StudentDashboard;